import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { workspaceZone, validZone } from './workspace-display';

// The booking engine's zone. Rep availability (slotScheduler) and the public
// booking form run on the API container's clock, pinned to Asia/Kolkata, so the
// calendar and the reschedule dialog must draw slots in it and say so. Every
// OTHER timestamp is the workspace's (fmtZoned).
export const SCHEDULING_ZONE = 'Asia/Kolkata';

/** Format a date in a named zone. '-' for no date. */
export function fmtInZone(value, zone, pattern) {
  if (!value) return '-';
  // A bare 'YYYY-MM-DD' is already a calendar day: drawn as it is, never
  // moved through a midnight in some zone (the API sends DATE columns so).
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return format(parseISO(value), pattern);
  const d = typeof value === 'string' ? parseISO(value) : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return formatInTimeZone(d, validZone(zone) ? zone : SCHEDULING_ZONE, pattern);
}

/**
 * Format a date on the WORKSPACE's wall clock (Settings > Workspace time
 * zone), whatever the viewer's browser is set to. Server stores UTC.
 * This was fmtIST, which drew every workspace in India time: a London store's
 * 09:00 order read 13:30.
 *
 * @example
 *   fmtZoned(lead.created_at, 'd MMM yyyy h:mm a')   -> "27 May 2026 6:30 PM"
 */
export function fmtZoned(value, pattern) {
  return fmtInZone(value, workspaceZone(), pattern);
}

/**
 * cn - merges Tailwind classes intelligently.
 * Use this anywhere you build className strings conditionally.
 *
 * @example
 * cn('px-2 py-1', isActive && 'bg-primary', className)
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Money: lib/money (formatMoney / money), in the workspace's currency.

/**
 * Initials from a full name (for avatars).
 */
// By letter, not by UTF-16 unit: "QC 🍪 Émile" took half of the emoji's
// surrogate pair and drew a broken glyph. A word that starts with no letter
// (an emoji, a digit run) is skipped.
export function initials(name) {
  if (!name) return '?';
  const firstLetter = (w) => Array.from(w).find((ch) => /\p{L}/u.test(ch)) || '';
  const letters = String(name).trim().split(/\s+/).map(firstLetter).filter(Boolean);
  return (letters.slice(0, 2).join('')).toUpperCase() || '?';
}

// Display a person's name in Title Case ("JP ADDAKULA" -> "Jp Addakula"),
// preserving hyphens/apostrophes. Data is sometimes stored all-caps.
export function titleCase(name) {
  if (!name) return name;
  return String(name).toLowerCase().replace(/(^|[\s-'’])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());
}
